<script>
// Frontend for the payment-link session model.
//   - Templates: companies-get-payment-pages (deployed)
//   - List sessions: get-active-payment-link-sessions (deployed)
//   - Create / regenerate: create-payment-link-session (deployed)
//   - Send email: currently reuses the LEGACY transactions/{id}/payments/request
//     endpoint (parent owns the call), which emails /pay/{transaction.url_hash}
//     — the old questgenie URL — NOT /pay/{session.hash}. Plan is to extend
//     that lambda to accept an optional session hash so it can email the new
//     template-driven URL when one is configured.
//
// Gated by the parent (TransactionDetails.vue) — this component itself
// doesn't enforce the gate so it stays reusable when the gate is lifted.

import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui';
import { API_LOCATION } from '@/api-config';
import { formatGroupDate } from '@/plugins/dates';

const PAY_URL_BASE = 'https://offthecouch.io/pay/';

export default {
  name: 'PaymentRequestsDrawer',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
  props: {
    show: { type: Boolean, default: false },
    transactionId: { type: [Number, String], default: null },
    companyGroupId: { type: [Number, String], default: null },
    /** Pass-through from parent so the Deposit section can show current state. */
    transaction: { type: Object, default: () => ({}) },
    getAmount: { type: Function, default: (v) => `$${Number(v || 0).toFixed(2)}` },
    /** Parent gates this — payment_request email_settings active AND
     *  transaction is in a state that allows sending. Drawer disables the
     *  Send email row action when false. */
    canSendEmail: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      creating: false,
      regeneratingId: null,
      revokingId: null,
      pulseSessionId: null,
      pickerTemplateId: null,
      sessions: [],
      templates: [],
      // Inline deposit form state — seeded from transaction props.
      depositForm: {
        enabled: false,
        amount: null,
      },
      depositError: '',
      savingDeposit: false,
    };
  },
  computed: {
    urlBase() {
      return PAY_URL_BASE;
    },
    activeTemplates() {
      return this.templates.filter((t) => !t.is_archived);
    },
    defaultTemplate() {
      return this.activeTemplates.find((t) => t.is_default === 1) || null;
    },
    hasMultipleTemplates() {
      return this.activeTemplates.length > 1;
    },
    hasNoTemplates() {
      return this.activeTemplates.length === 0;
    },
    sortedSessions() {
      return [...this.sessions].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    },
  },
  watch: {
    show(open) {
      if (open) {
        this.load();
        this.seedDepositForm();
        document.addEventListener('keydown', this._onEscKey);
      } else {
        document.removeEventListener('keydown', this._onEscKey);
      }
    },
    // Re-seed local deposit state whenever the parent's transaction
    // refreshes (e.g. after a save round-trip).
    'transaction.custom_deposit_enabled'() {
      if (this.show) this.seedDepositForm();
    },
    'transaction.custom_deposit_amount'() {
      if (this.show) this.seedDepositForm();
    },
  },
  created() {
    this._onEscKey = (e) => {
      if (e.key === 'Escape' && this.show) {
        this.$emit('close');
      }
    };
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onEscKey);
  },
  methods: {
    payUrl(session) {
      return `${PAY_URL_BASE}${session.hash}`;
    },
    truncateHash(hash) {
      if (!hash || hash.length <= 14) return hash;
      return `${hash.slice(0, 8)}…${hash.slice(-4)}`;
    },
    formatExpires(session) {
      if (!session.expires_at) return 'Never expires';
      const formatted = formatGroupDate(session.expires_at, { dayOfWeek: false });
      if (!formatted) return '';
      return this.isExpired(session) ? `Expired ${formatted}` : `Expires ${formatted}`;
    },
    isExpired(session) {
      if (!session.expires_at) return false;
      return new Date(session.expires_at).getTime() <= Date.now();
    },
    pulse(sessionId) {
      this.pulseSessionId = sessionId;
      setTimeout(() => {
        if (this.pulseSessionId === sessionId) this.pulseSessionId = null;
      }, 1200);
    },
    async writeUrlToClipboard(session) {
      try {
        await navigator.clipboard.writeText(this.payUrl(session));
        this.pulse(session.id);
        return true;
      } catch (e) {
        this.$notify({
          type: 'danger',
          message: 'Could not copy. Try again.',
          icon: 'tim-icons icon-alert-circle-exc',
        });
        return false;
      }
    },
    async copyLink(session) {
      const ok = await this.writeUrlToClipboard(session);
      if (ok) {
        this.$notify({
          type: 'success',
          message: 'Payment link copied',
          icon: 'tim-icons icon-check-2',
        });
      }
    },
    openLink(session) {
      window.open(this.payUrl(session), '_blank', 'noopener,noreferrer');
    },
    async getAuthToken() {
      const data = await Auth.currentSession();
      return data.idToken.jwtToken;
    },
    async load() {
      this.loading = true;
      await Promise.all([this.loadTemplates(), this.loadSessions()]);
      this.loading = false;
    },
    sessionsUrl() {
      const companyId = localStorage.getItem('companyId');
      return `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/transactions/${this.transactionId}/payment-link-sessions`;
    },
    async loadTemplates() {
      try {
        const token = await this.getAuthToken();
        const companyId = localStorage.getItem('companyId');
        const url = `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/payment-pages`;
        const res = await axios.get(url, { headers: { Authorization: token } });
        const all = (res.data && res.data.payment_pages) || [];
        // Only link-type templates; drop archived rows.
        // payment_pages.archived (NOT is_archived) is the actual column.
        this.templates = all.filter((p) => p.type === 'link' && p.archived !== 1);
        if (this.defaultTemplate) {
          this.pickerTemplateId = this.defaultTemplate.id;
        }
      } catch (err) {
        console.log(err);
        this.templates = [];
      }
    },
    async loadSessions() {
      if (!this.transactionId) {
        this.sessions = [];
        return;
      }
      try {
        const token = await this.getAuthToken();
        const res = await axios.get(this.sessionsUrl(), {
          headers: { Authorization: token },
        });
        this.sessions = (res.data && res.data.sessions) || [];
      } catch (err) {
        console.log(err);
        this.sessions = [];
        this.notifyError(err, 'Could not load payment requests');
      }
    },
    async generate(pageId) {
      this.creating = true;
      try {
        const token = await this.getAuthToken();
        const body = {};
        if (pageId) body.page_id = pageId;
        const res = await axios.post(this.sessionsUrl(), body, {
          headers: { Authorization: token },
        });
        const session = res.data && res.data.session;
        if (!session) throw new Error('Malformed response');
        const idx = this.sessions.findIndex((s) => s.id === session.id);
        if (idx !== -1) this.sessions.splice(idx, 1, session);
        else this.sessions.push(session);
        await this.writeUrlToClipboard(session);
        this.$notify({
          type: 'success',
          message: session.reused
            ? 'Active payment link already exists — copied'
            : 'New payment link generated and copied',
          icon: 'tim-icons icon-check-2',
        });
      } catch (err) {
        console.log(err);
        this.notifyError(err, 'Could not generate payment request');
      } finally {
        this.creating = false;
      }
    },
    async regenerate(session) {
      this.regeneratingId = session.id;
      try {
        const token = await this.getAuthToken();
        const res = await axios.post(
          this.sessionsUrl(),
          {
            page_id: session.payment_page_id,
            regenerate_session_id: session.id,
          },
          { headers: { Authorization: token } },
        );
        const replacement = res.data && res.data.session;
        if (!replacement) throw new Error('Malformed response');
        const idx = this.sessions.findIndex((s) => s.id === session.id);
        if (idx !== -1) this.sessions.splice(idx, 1, replacement);
        else this.sessions.push(replacement);
        await this.writeUrlToClipboard(replacement);
        this.$notify({
          type: 'success',
          message: 'Payment link regenerated and copied',
          icon: 'tim-icons icon-check-2',
        });
      } catch (err) {
        console.log(err);
        this.notifyError(err, 'Could not regenerate payment request');
      } finally {
        this.regeneratingId = null;
      }
    },
    onRowCommand(cmd, session) {
      switch (cmd) {
        case 'open':       this.openLink(session); break;
        case 'email':      this.$emit('send-email-prompt', session); break;
        case 'regenerate': this.regenerate(session); break;
        case 'revoke':     this.revokePrompt(session); break;
      }
    },
    canRevoke(session) {
      // Allow opening the kebab on expired sessions just so Revoke is
      // reachable — Copy/Open/Email/Regenerate stay disabled inside.
      return session.is_template_archived !== 1;
    },
    revokePrompt(session) {
      // Destructive — the link stops working immediately for the customer.
      // Use swal for the confirmation, matching legacy patterns in the
      // transactions page.
      swal({
        title: 'Revoke payment link?',
        html: '<small>The customer will no longer be able to use this payment link. This cannot be undone.</small>',
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#1d8cf8',
        confirmButtonColor: '#fd5d93',
        confirmButtonText: 'Revoke',
      }).then((response) => {
        if (response.value === true) {
          this.revoke(session);
        }
      });
    },
    async revoke(session) {
      this.revokingId = session.id;
      try {
        const token = await this.getAuthToken();
        await axios.post(
          this.sessionsUrl(),
          { revoke_session_id: session.id },
          { headers: { Authorization: token } },
        );
        // Optimistically remove from the visible list. The server is
        // idempotent, so a refresh would show the same state.
        const idx = this.sessions.findIndex((s) => s.id === session.id);
        if (idx !== -1) this.sessions.splice(idx, 1);
        this.$notify({
          type: 'success',
          message: 'Payment link revoked',
          icon: 'tim-icons icon-check-2',
        });
      } catch (err) {
        console.log(err);
        this.notifyError(err, 'Could not revoke payment link');
      } finally {
        this.revokingId = null;
      }
    },
    notifyError(err, fallback) {
      let message = fallback;
      try {
        const data = err?.response?.data;
        if (typeof data === 'string' && data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed && parsed.error) message = parsed.error;
            else message = data;
          } catch (e) {
            message = data;
          }
        } else if (data && data.error) {
          message = data.error;
        }
      } catch (e) {
        // Fall through to fallback.
      }
      this.$notify({
        type: 'danger',
        message,
        icon: 'tim-icons icon-alert-circle-exc',
      });
    },
    seedDepositForm() {
      const tx = this.transaction || {};
      this.depositForm.enabled = tx.custom_deposit_enabled === 1;
      this.depositForm.amount =
        tx.custom_deposit_amount != null ? Number(tx.custom_deposit_amount) : null;
      this.depositError = '';
    },
    onDepositAmountInput(val) {
      this.depositForm.amount = val;
      if (this.depositError) this.depositError = '';
    },
    async saveDeposit() {
      if (
        this.depositForm.enabled &&
        (this.depositForm.amount == null || Number(this.depositForm.amount) <= 0)
      ) {
        this.depositError = 'Enter an amount greater than 0';
        return;
      }
      this.depositError = '';
      this.savingDeposit = true;
      try {
        const token = await this.getAuthToken();
        const companyId = localStorage.getItem('companyId');
        const url = `${API_LOCATION}companies/${companyId}/groups/${this.companyGroupId}/transactions/${this.transactionId}/deposit`;
        await axios.post(
          url,
          {
            custom_deposit_enabled: this.depositForm.enabled,
            custom_deposit_amount: this.depositForm.amount,
          },
          { headers: { Authorization: token } },
        );
        this.$notify({
          type: 'success',
          message: 'Custom deposit saved',
          icon: 'tim-icons icon-check-2',
        });
        // Tell the parent to refresh — this.transaction is a prop, and the
        // parent owns the source of truth (its getTransaction()).
        this.$emit('deposit-saved');
      } catch (err) {
        console.log(err);
        this.$notify({
          type: 'danger',
          message: 'Could not save custom deposit',
          icon: 'tim-icons icon-alert-circle-exc',
        });
      } finally {
        this.savingDeposit = false;
      }
    },
  },
};
</script>

<template>
  <base-drawer
    :show="show"
    title="Payment requests"
    width="560px"
    @close="$emit('close')"
  >
    <!-- ─── Active payment requests ──────────────────────────────────── -->
    <!-- TEMPORARILY HIDDEN: remove `v-if="false"` to restore. -->
    <drawer-section v-if="false" title="Active payment requests" icon="icon-link-72">
      <template #header-actions>
        <span v-if="sortedSessions.length" class="active-count">
          {{ sortedSessions.length }}
        </span>
      </template>

      <div v-if="loading" class="session-skel-list" aria-busy="true">
        <div
          v-for="i in 2"
          :key="`skel-${i}`"
          class="session-skel"
        >
          <div class="session-skel__head">
            <span class="skel" style="width: 140px; height: 14px;"></span>
            <span class="skel skel--badge"></span>
          </div>
          <span class="skel" style="display: block; width: 240px; height: 11px; margin-top: 6px;"></span>
          <span class="skel" style="display: block; width: 110px; height: 10px; margin-top: 6px;"></span>
          <div class="session-skel__actions">
            <span class="skel" style="width: 64px; height: 28px;"></span>
            <span class="skel" style="width: 64px; height: 28px;"></span>
            <span class="skel" style="width: 96px; height: 28px;"></span>
          </div>
        </div>
      </div>

      <div v-else-if="!sortedSessions.length" class="empty-state">
        <i class="tim-icons icon-link-72 empty-state__icon"></i>
        <p class="empty-state__title text-white mb-1">No active payment requests</p>
        <p class="empty-state__sub mb-0">
          <template v-if="hasNoTemplates">
            Set up a payment page template first to generate a request.
          </template>
          <template v-else-if="hasMultipleTemplates">
            Pick a template below to create one.
          </template>
          <template v-else>
            Create one to share with the customer.
          </template>
        </p>
      </div>

      <ul v-else class="session-list">
        <li
          v-for="session in sortedSessions"
          :key="session.id"
          class="session-row"
          :class="{
            'session-row--pulse': pulseSessionId === session.id,
            'session-row--expired': isExpired(session),
            'session-row--archived': session.is_template_archived,
          }"
        >
          <div class="session-row__main">
            <div class="session-row__head">
              <span class="session-row__template text-white">
                {{ session.page_name }}
              </span>
              <span
                v-if="session.is_template_archived"
                class="session-row__chip session-row__chip--warn"
              >
                Template archived
              </span>
              <span
                v-else-if="isExpired(session)"
                class="session-row__chip session-row__chip--danger"
              >
                Expired
              </span>
              <span v-else class="session-row__chip session-row__chip--active">
                Active
              </span>
            </div>
            <p class="session-row__url" :title="payUrl(session)">
              {{ urlBase }}<span class="session-row__hash">{{ truncateHash(session.hash) }}</span>
            </p>
            <p class="session-row__expires">{{ formatExpires(session) }}</p>
          </div>

          <div class="session-row__actions">
            <base-button
              size="sm"
              class="pl-action-btn pl-action-btn--primary"
              @click="copyLink(session)"
              :disabled="isExpired(session)"
            >
              <i class="tim-icons icon-single-copy-04 mr-1" style="font-size: 11px;"></i>
              Copy link
            </base-button>

            <el-dropdown
              placement="bottom-end"
              trigger="click"
              @command="(cmd) => onRowCommand(cmd, session)"
            >
              <base-button
                size="sm"
                class="pl-action-btn pl-action-btn--icon"
                :disabled="isExpired(session) && !canRevoke(session)"
                aria-label="More actions"
              >
                <i class="tim-icons icon-bullet-list-67"></i>
              </base-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  command="open"
                  :disabled="isExpired(session)"
                >
                  <i class="tim-icons icon-attach-87 row-menu__icon"></i>
                  Open in new tab
                </el-dropdown-item>
                <el-dropdown-item
                  command="email"
                  :disabled="!canSendEmail || isExpired(session)"
                >
                  <i class="tim-icons icon-email-85 row-menu__icon"></i>
                  Send via email
                </el-dropdown-item>
                <el-dropdown-item
                  command="regenerate"
                  :disabled="regeneratingId === session.id || session.is_template_archived"
                >
                  <i
                    class="tim-icons row-menu__icon"
                    :class="regeneratingId === session.id ? 'icon-refresh-02 spin' : 'icon-refresh-01'"
                  ></i>
                  Regenerate link
                </el-dropdown-item>
                <el-dropdown-item
                  command="revoke"
                  divided
                  :disabled="revokingId === session.id"
                  class="row-menu__danger"
                >
                  <i class="tim-icons icon-simple-remove row-menu__icon"></i>
                  Revoke link
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </li>
      </ul>

      <!-- Generate UI consolidated into the same section. Renders below
           the session list (or empty state) with a thin divider above. -->
      <div v-if="!loading" class="generate-footer">
        <p v-if="hasNoTemplates" class="generate-empty">
          No payment page templates of type "link" are configured for this group.
        </p>

        <template v-else>
          <el-select
            v-if="hasMultipleTemplates"
            v-model="pickerTemplateId"
            placeholder="Pick a template"
            size="small"
            class="generate-select"
          >
            <el-option
              v-for="t in activeTemplates"
              :key="t.id"
              :value="t.id"
              :label="t.name + (t.is_default === 1 ? ' (default)' : '')"
            />
          </el-select>

          <base-button
            size="sm"
            class="pl-action-btn"
            :disabled="creating || (hasMultipleTemplates ? !pickerTemplateId : !defaultTemplate)"
            @click="generate(hasMultipleTemplates ? pickerTemplateId : null)"
          >
            <i
              class="tim-icons mr-1"
              style="font-size: 11px;"
              :class="creating ? 'icon-refresh-02 spin' : 'icon-simple-add'"
            ></i>
            {{ creating ? 'Generating…' : 'Generate and copy new request' }}
          </base-button>
        </template>
      </div>
    </drawer-section>

    <!-- ─── Deposit settings (inline editor) ─────────────────────────── -->
    <drawer-section title="Deposit settings" icon="icon-coins">
      <div class="deposit-edit">
        <info-hint>
          Specify a custom deposit amount shown on payment requests, overriding
          other deposit settings. When enabled, the request's expiration is
          based on the custom deposit amount instead of the transaction total.
        </info-hint>

        <div class="deposit-toggle">
          <el-switch
            v-model="depositForm.enabled"
            active-color="#e14eca"
          />
          <span class="text-white">Enable custom deposit</span>
        </div>

        <div v-if="depositForm.enabled" class="deposit-amount-row">
          <label class="deposit-amount-label">Custom deposit amount</label>
          <div class="deposit-amount-field">
            <base-input :error="depositError" class="mb-0">
              <money-input
                :value="depositForm.amount"
                :min="0"
                :precision="2"
                placeholder="0.00"
                @input="onDepositAmountInput"
              />
            </base-input>
          </div>
        </div>

        <div class="deposit-actions">
          <base-button
            size="sm"
            class="pl-action-btn"
            :disabled="savingDeposit"
            @click="saveDeposit"
          >
            <i
              class="tim-icons mr-1"
              style="font-size: 11px;"
              :class="savingDeposit ? 'icon-refresh-02 spin' : 'icon-check-2'"
            ></i>
            {{ savingDeposit ? 'Saving…' : 'Save' }}
          </base-button>
        </div>
      </div>
    </drawer-section>
  </base-drawer>
</template>

<style scoped>
/* ─── Deposit section (inline editor) ─────────────────────────────────── */
.deposit-edit {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.deposit-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13.5px;
}
.deposit-amount-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.deposit-amount-label {
  margin: 0;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}
.deposit-amount-field {
  max-width: 220px;
}
.deposit-actions {
  display: flex;
  justify-content: flex-end;
}

/* ─── Active links section ─────────────────────────────────────────────── */
.active-count {
  display: inline-block;
  min-width: 22px;
  padding: 1px 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(225, 78, 202, 0.18);
  text-align: center;
  line-height: 1.6;
}

.empty-state {
  text-align: center;
  padding: 12px 0;
}
.empty-state__icon {
  font-size: 24px;
  color: rgba(225, 78, 202, 0.65);
  display: block;
  margin: 0 auto 10px;
}
.empty-state__title {
  font-size: 14px;
  font-weight: 500;
}
.empty-state__sub {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.6);
}

.session-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

/* Skeleton list — no row border/bg so we don't nest a card inside the
 * section content. Just shimmer placeholders stacked. */
.session-skel-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.session-skel {
  display: flex;
  flex-direction: column;
}
.session-skel__head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.session-skel__actions {
  display: flex;
  gap: 6px;
  margin-top: 12px;
}

.session-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 0;
  transition: background 0.3s ease;
}

/* Separator between rows — never on the first. Section header already
 * sits above so no top-divider needed there. */
.session-row + .session-row {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Pulse: short pink wash after copy/create. Spans the full content area
 * (negative margins compensate for DrawerSection padding) so the feedback
 * is unmistakable without redrawing a border. */
.session-row--pulse {
  background: rgba(225, 78, 202, 0.12);
  margin: 0 -16px;
  padding-left: 16px;
  padding-right: 16px;
}

.session-row--expired { opacity: 0.55; }

/* Archived just dims the row text; the "Template archived" chip carries
 * the warning signal. No background tint needed. */
.session-row--archived .session-row__template,
.session-row--archived .session-row__url,
.session-row--archived .session-row__expires {
  opacity: 0.7;
}

.session-row__main { width: 100%; min-width: 0; }

.session-row__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.session-row__template { font-size: 14px; font-weight: 500; }

.session-row__chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.5;
}
.session-row__chip--active { background: rgba(0, 242, 195, 0.16); color: #00f2c3; }
.session-row__chip--danger { background: rgba(253, 93, 147, 0.16); color: #fd5d93; }
.session-row__chip--warn   { background: rgba(245, 158, 11, 0.16); color: #f59e0b; }

.session-row__url {
  margin: 0 0 2px;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.session-row__hash { color: rgba(255, 255, 255, 0.85); }
.session-row__expires {
  margin: 0;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.5);
}

.session-row__actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  width: 100%;
}

/* ─── Generate footer (lives at the bottom of the Active section) ─────── */
.generate-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.generate-empty {
  margin: 0;
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
}

.generate-select {
  flex: 0 0 180px;
}

/* ─── Action button (mirrors BookingDrawer .neutral-action-btn) ───────── */
.pl-action-btn {
  flex-shrink: 0;
  background: transparent !important;
  border: 1px solid rgba(225, 78, 202, 0.7) !important;
  color: #e14eca !important;
  font-size: 12px !important;
  padding: 6px 14px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.pl-action-btn:hover:not(:disabled) {
  background: rgba(225, 78, 202, 0.15) !important;
  border-color: rgba(225, 78, 202, 0.85) !important;
}
.pl-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Icon-only variant — square button, single icon centered. Used for row
 * actions so 5 buttons fit on one line. */
.pl-action-btn--icon {
  padding: 6px 8px !important;
  min-width: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pl-action-btn--icon i {
  font-size: 13px !important;
  margin: 0 !important;
}

/* Row-menu dropdown items — icon + label spacing, plus the danger
 * styling for the Revoke entry. */
.row-menu__icon {
  font-size: 13px;
  margin-right: 8px;
  width: 14px;
  display: inline-block;
  text-align: center;
}
::v-deep .row-menu__danger {
  color: #fd5d93;
}
::v-deep .row-menu__danger:hover:not(.is-disabled) {
  background: rgba(253, 93, 147, 0.08);
  color: #fd5d93;
}

/* ─── Spinner ──────────────────────────────────────────────────────────── */
.spin {
  animation: pr-spin 0.8s linear infinite;
  display: inline-block;
}
@keyframes pr-spin {
  to { transform: rotate(360deg); }
}
</style>
